import { cn } from '../../lib/cn';

/**
 * IconLabelFactory — custom brand glyph for IGNODE Label Factory.
 *
 * Composition:
 *   • 4 L-shaped corner brackets forming a bounding box (the canonical
 *     annotation/labelling tool motif)
 *   • Central dot — the labelled object
 *   • Tag/ribbon attached to the top-right corner — the label itself
 *
 * Renders in `currentColor` so it inherits the parent text color. Designed
 * to sit inside the gradient brand square (indigo→blue), which uses
 * `text-white`. Stroke-based so it scales cleanly at any size.
 */
interface IconLabelFactoryProps {
    className?: string;
    strokeWidth?: number;
}

export default function IconLabelFactory({ className, strokeWidth = 2 }: IconLabelFactoryProps) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cn('shrink-0', className)}
            aria-hidden="true"
        >
            {/* Bounding-box corner brackets — pushed to the edges of the
                24×24 viewBox (corners at 2 and 22) so the box reads as
                ~83% of the available area. L-legs lengthened to 8 px each
                so the corners feel substantial. */}
            <path d="M2 10 L2 2 L10 2" />
            <path d="M2 14 L2 22 L10 22" />
            <path d="M22 14 L22 22 L14 22" />
            {/* Top-right corner is replaced by the tag ribbon below */}

            {/* Label tag/ribbon attached to top-right corner of the bbox */}
            <path d="M14 2 L22 2 L22 8 L18 12 L14 12 Z" fill="currentColor" fillOpacity="0.25" />
            {/* tag pin hole */}
            <circle cx="19" cy="5.25" r="0.8" fill="currentColor" />

            {/* Central labelled object — filled dot scaled up to stay
                proportional inside the larger bbox. */}
            <circle cx="12" cy="13" r="2.25" fill="currentColor" />
        </svg>
    );
}
